import { useChains } from "@opensea/app-kit"
import type { FragmentOf } from "@opensea/graphql"
import { graphql, readFragment, useQuery } from "@opensea/graphql"
import {
  BottomSheetBody,
  BottomSheetFooter,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
  Checkbox,
  FlexEnd,
  FlexStart,
  Label,
  SkeletonLine,
  TextBody,
} from "@opensea/ui-kit"
import { useState } from "react"
import { useTranslations } from "use-intl"
import { ActionTimeline } from "../../../components/ActionTimeline"
import { ActionTimelineFragment } from "../../../components/ActionTimeline/ActionTimeline.fragment"
import { Button } from "../../../components/Button"
import { TransactionFullScreen } from "../../../components/TransactionFullScreen"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants"
import { useAddress } from "../../../polychain/hooks/useAddress"
import type { CancelAllOrdersFlowState } from "../../flow-state"
import { useAllActionsCompleted } from "../../hooks/useAllActionsCompleted"
import { useEndFlowOnAccountChange } from "../../hooks/useEndFlowOnAccountChange"
import { useFlowFinalTransactionStatus } from "../../hooks/useFlowFinalTransactionStatus"

type Props = Pick<CancelAllOrdersFlowState, "chain">
const MakerOrdersCountFragment = graphql(`
  fragment MakerOrdersCount on MakerOrdersCountResponse {
    chain
    activeListings
    inactiveListings
    offers
  }
`)
const CancelAllOrdersQuery = graphql(
  `
    query CancelAllOrdersFlowQuery(
      $chain: ChainIdentifier!
      $address: Address!
    ) {
      cancelAllOrders(chain: $chain, address: $address) {
        actions {
          ...ActionTimeline
        }
      }
      makerOrdersCount(address: $address, chain: $chain) {
        ...MakerOrdersCount
      }
    }
  `,
  [ActionTimelineFragment, MakerOrdersCountFragment],
)

export const CancelAllOrdersFlow = ({ chain }: Props) => {
  const address = useAddress()
  const t = useTranslations("wallet.CancelAllOrdersFlow")
  const [{ data, fetching }] = useQuery({
    query: CancelAllOrdersQuery,
    variables: {
      chain,
      address: address ?? "",
    },
    pause: !address,
    context: NETWORK_ONLY_OPERATION_CONTEXT,
  })
  useEndFlowOnAccountChange()
  const { getName: getChainName } = useChains()
  const [isUserAgreed, setIsUserAgreed] = useState(false)
  const [hasConfirmed, setHasConfirmed] = useState(false)

  const [allActionsCompleted] = useAllActionsCompleted()
  const chainName = getChainName(chain)
  if (fetching) {
    return <CancelAllOrdersFlowSkeleton chainName={chainName} />
  }

  if (allActionsCompleted) {
    return <CancelAllOrdersStatus />
  }

  return (
    <>
      <CancelAllOrdersFlowHeader chainName={chainName} />

      <CancelAllOrdersFlowBody
        chainName={chainName}
        hasStartedActions={hasConfirmed}
        isUserAgreed={isUserAgreed}
        makerOrdersCount={data?.makerOrdersCount?.[0]}
        setIsUserAgreed={setIsUserAgreed}
      />

      <BottomSheetFooter>
        {hasConfirmed && data?.cancelAllOrders.actions ? (
          <ActionTimeline actions={data.cancelAllOrders.actions} />
        ) : (
          <FlexEnd>
            <Button
              disabled={!isUserAgreed}
              onClick={() => setHasConfirmed(true)}
              variant="destructive"
            >
              {t("confirmCancellation")}
            </Button>
          </FlexEnd>
        )}
      </BottomSheetFooter>
    </>
  )
}
const CancelAllOrdersFlowBody = ({
  chainName,
  makerOrdersCount,
  isUserAgreed,
  setIsUserAgreed,
  hasStartedActions,
}: {
  chainName: string
  makerOrdersCount?: FragmentOf<typeof MakerOrdersCountFragment> | null
  isUserAgreed: boolean
  setIsUserAgreed: (value: boolean) => void
  hasStartedActions: boolean
}) => {
  const t = useTranslations("wallet.CancelAllOrdersFlow")
  const orderCounts = readFragment(MakerOrdersCountFragment, makerOrdersCount)

  let cancellationMessage
  if (orderCounts) {
    const { activeListings, inactiveListings, offers } = orderCounts
    const hasActiveListings = activeListings > 0
    const hasInactiveListings = inactiveListings > 0
    const hasOffers = offers > 0

    if (hasActiveListings && hasInactiveListings && hasOffers) {
      cancellationMessage = t(
        "attemptingToCancelActiveInactiveListingsAndOffersV2",
        {
          activeCount: activeListings,
          inactiveCount: inactiveListings,
          offersCount: offers,
        },
      )
    } else if (hasActiveListings && hasInactiveListings) {
      cancellationMessage = t("attemptingToCancelActiveAndInactiveListingsV2", {
        activeCount: activeListings,
        inactiveCount: inactiveListings,
      })
    } else if (hasActiveListings && hasOffers) {
      cancellationMessage = t("attemptingToCancelActiveListingsAndOffersV2", {
        activeCount: activeListings,
        offersCount: offers,
      })
    } else if (hasInactiveListings && hasOffers) {
      cancellationMessage = t("attemptingToCancelInactiveListingsAndOffersV2", {
        inactiveCount: inactiveListings,
        offersCount: offers,
      })
    } else if (hasActiveListings) {
      cancellationMessage = t("attemptingToCancelActiveListings", {
        activeCount: activeListings,
      })
    } else if (hasInactiveListings) {
      cancellationMessage = t("attemptingToCancelInactiveListings", {
        inactiveCount: inactiveListings,
      })
    } else if (hasOffers) {
      cancellationMessage = t("attemptingToCancelOffers", {
        offersCount: offers,
      })
    } else {
      cancellationMessage = t("attemptingToCancelAll")
    }
  } else {
    cancellationMessage = t("attemptingToCancelAll")
  }

  return (
    <BottomSheetBody className="flex flex-col gap-6">
      <TextBody className="text-text-secondary">{cancellationMessage}</TextBody>
      <FlexStart className="items-center">
        <Checkbox
          checked={isUserAgreed}
          className="size-5"
          disabled={hasStartedActions}
          id="cancel-all-orders-confirmation"
          onClick={() => setIsUserAgreed(!isUserAgreed)}
        />
        &nbsp;&nbsp;
        <Label
          htmlFor="cancel-all-orders-confirmation"
          size="sm"
          weight="regular"
        >
          {t("confirmIntention", { chainName })}
        </Label>
      </FlexStart>
    </BottomSheetBody>
  )
}
const CancelAllOrdersFlowHeader = ({ chainName }: { chainName: string }) => {
  const t = useTranslations("wallet.CancelAllOrdersFlow")
  return (
    <BottomSheetHeader>
      <BottomSheetHeaderTitle>
        {t("headerTitle", { chainName })}
      </BottomSheetHeaderTitle>
    </BottomSheetHeader>
  )
}

export const CancelAllOrdersFlowSkeleton = ({
  chainName,
}: {
  chainName: string
}) => {
  return (
    <>
      <CancelAllOrdersFlowHeader chainName={chainName} />
      <BottomSheetBody className="flex flex-col gap-6">
        <SkeletonLine className="h-5 w-[300px]" />
        <SkeletonLine className="h-5 w-[300px]" />
      </BottomSheetBody>
      <BottomSheetFooter>
        <FlexEnd>
          <SkeletonLine className="h-10 w-[224px]" />
        </FlexEnd>
      </BottomSheetFooter>
    </>
  )
}

const CancelAllOrdersStatus = () => {
  const t = useTranslations("wallet.CancelAllOrdersFlow")
  const status = useFlowFinalTransactionStatus()

  if (status === "pending") {
    return <TransactionFullScreen title={t("status.pending")} />
  }

  if (status === "success") {
    return <TransactionFullScreen title={t("status.success")} />
  }

  return <TransactionFullScreen title={t("status.error")} />
}
